import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getApiFailureMessage, getUnifiedApiErrorMessage } from '../utils/apiErrorMessage';
import './RegisterForm.css';

// WebAuthn 타입 정의
declare global {
  interface Window {
    PublicKeyCredential: any;
  }
}

interface RegisterFormProps {
  onSuccess?: () => void;
  onSwitchToLogin?: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess, onSwitchToLogin }) => {
  const { register, login, isLoading, error, clearError } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    name: '',
    nickname: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const [registerPasskey, setRegisterPasskey] = useState(false);

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.username.trim()) {
      errors.username = '계정 ID를 입력해주세요.';
    } else if (formData.username.length < 2) {
      errors.username = '계정 ID는 2자 이상이어야 합니다.';
    }

    if (!formData.email.trim()) {
      errors.email = '이메일을 입력해주세요.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = '올바른 이메일 형식을 입력해주세요.';
    }

    if (!formData.name.trim()) {
      errors.name = '이름을 입력해주세요.';
    } else if (formData.name.length < 2) {
      errors.name = '이름은 2자 이상이어야 합니다.';
    }

    if (!formData.nickname.trim()) {
      errors.nickname = '닉네임을 입력해주세요.';
    } else if (formData.nickname.length < 2) {
      errors.nickname = '닉네임은 2자 이상이어야 합니다.';
    }

    if (!formData.phone.trim()) {
      errors.phone = '전화번호를 입력해주세요.';
    } else {
      // 하이픈 제거 후 숫자만 검증
      const phoneNumbers = formData.phone.replace(/[^\d]/g, '');
      if (phoneNumbers.length < 10 || phoneNumbers.length > 11) {
        errors.phone = '전화번호는 10자리 또는 11자리여야 합니다.';
      } else if (!/^01[0-9]/.test(phoneNumbers)) {
        errors.phone = '전화번호는 010, 011, 016, 017, 018, 019로 시작해야 합니다.';
      }
    }

    // 비밀번호 검증 (필수)
    if (!formData.password.trim()) {
      errors.password = '비밀번호를 입력해주세요.';
    } else if (formData.password.length < 6) {
      errors.password = '비밀번호는 6자 이상이어야 합니다.';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = '비밀번호가 일치하지 않습니다.';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 전화번호 포맷팅 함수 (숫자만 입력받아 자동으로 하이픈 추가)
  // 형식: 010-1234-5678 (11자리) 또는 010-123-4567 (10자리)
  const formatPhoneNumber = (value: string): string => {
    const numbers = value.replace(/[^\d]/g, '');
    const limitedNumbers = numbers.slice(0, 11);

    if (limitedNumbers.length <= 3) {
      return limitedNumbers;
    } else if (limitedNumbers.length <= 7) {
      // 4~7자리: 두 번째 하이픈은 붙이지 않음 (뒷자리 없을 때 trailing hyphen 방지)
      return `${limitedNumbers.slice(0, 3)}-${limitedNumbers.slice(3)}`;
    } else if (limitedNumbers.length === 10) {
      // 10자리: 010-123-4567
      return `${limitedNumbers.slice(0, 3)}-${limitedNumbers.slice(3, 6)}-${limitedNumbers.slice(6)}`;
    } else {
      // 8~9자리, 11자리: 010-1234-5678
      return `${limitedNumbers.slice(0, 3)}-${limitedNumbers.slice(3, 7)}-${limitedNumbers.slice(7)}`;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // 전화번호 필드인 경우 자동 포맷팅
    let processedValue = value;
    if (name === 'phone') {
      processedValue = formatPhoneNumber(value);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // 입력 시 에러 초기화
    if (error) {
      clearError();
    }
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // 일반 회원가입
    const registerSuccess = await register({
      username: formData.username.trim(),
      email: formData.email.trim(),
      nickname: formData.nickname.trim(),
      phone: formData.phone.trim() || undefined,
      password: formData.password.trim()
    });

    if (!registerSuccess) {
      return; // 회원가입 실패 시 종료
    }

    // 회원가입 후 자동 로그인
    const loginSuccess = await login({
      username: formData.username.trim(),
      password: formData.password.trim()
    });

    if (!loginSuccess) {
      // 로그인 실패 시에도 회원가입은 완료되었으므로 성공 처리
      if (onSuccess) {
        onSuccess();
      }
      return;
    }

    // 패스키 등록을 선택한 경우
    if (registerPasskey) {
      // WebAuthn 지원 확인
      if (!window.PublicKeyCredential) {
        alert('이 브라우저는 패스키를 지원하지 않습니다. 최신 브라우저를 사용해주세요.');
        if (onSuccess) {
          onSuccess();
        }
        return;
      }

      try {
        // 로그인된 상태에서 패스키 추가
        const { apiService } = await import('../services/api');
        const { authService } = await import('../services/authService');
        
        // 1. 패스키 추가 시작
        const startResponse = await apiService.api.post('/auth/webauthn/credentials/add/start', null, {
          params: { deviceName: '기본 디바이스' }
        });

        if (!startResponse.data.success || !startResponse.data.data) {
          throw new Error(getUnifiedApiErrorMessage(startResponse.data));
        }

        const challengeData = startResponse.data.data;

        // 2. 브라우저에서 Credential 생성
        const publicKeyCredentialCreationOptions: PublicKeyCredentialCreationOptions = {
          challenge: authService.base64UrlToArrayBuffer(challengeData.challenge),
          rp: {
            id: challengeData.rp.id,
            name: challengeData.rp.name
          },
          user: {
            id: authService.base64UrlToArrayBuffer(challengeData.user.id),
            name: challengeData.user.name,
            displayName: challengeData.user.displayName
          },
          pubKeyCredParams: challengeData.pubKeyCredParams.map((param: any) => ({
            type: param.type as PublicKeyCredentialType,
            alg: param.alg
          })),
          timeout: challengeData.timeout,
          attestation: challengeData.attestation as AttestationConveyancePreference,
          authenticatorSelection: {
            userVerification: challengeData.authenticatorSelection.userVerification as UserVerificationRequirement,
            residentKey: challengeData.authenticatorSelection.residentKey as ResidentKeyRequirement
          }
        };

        const credential = await navigator.credentials.create({
          publicKey: publicKeyCredentialCreationOptions
        }) as PublicKeyCredential | null;

        if (!credential || !(credential instanceof PublicKeyCredential)) {
          throw new Error('인증기 생성에 실패했습니다.');
        }

        const response = credential.response as AuthenticatorAttestationResponse;

        // 3. Credential을 Base64로 변환
        const credentialId = authService.arrayBufferToBase64Url(credential.rawId);
        const clientDataJSON = authService.arrayBufferToBase64Url(response.clientDataJSON);
        const attestationObject = authService.arrayBufferToBase64Url(response.attestationObject);
        const transports = response.getTransports ? response.getTransports() : undefined;

        // 4. 패스키 추가 완료
        const finishResponse = await apiService.api.post('/auth/webauthn/credentials/add/finish', {
          challenge: challengeData.challenge,
          credential: {
            id: credentialId,
            rawId: credentialId,
            response: {
              clientDataJSON,
              attestationObject,
              transports
            },
            type: credential.type,
            authenticatorAttachment: credential.authenticatorAttachment || undefined
          }
        });

        if (!finishResponse.data.success) {
          throw new Error(getUnifiedApiErrorMessage(finishResponse.data));
        }
      } catch (error: any) {
        console.error('패스키 등록 오류:', error);
        alert(getApiFailureMessage(error, '패스키 등록 중 오류가 발생했습니다. 회원가입은 완료되었습니다.'));
      }
    }

    // 회원가입 완료 (패스키 등록 성공/실패 여부와 관계없이)
    if (onSuccess) {
      onSuccess();
    }
  };

  return (
    <div className="register-form-container">
      <div className="register-form">
        <h2>회원가입</h2>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">계정 ID <span className="required-mark">*</span></label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              placeholder="로그인에 사용할 계정 ID를 입력하세요"
              required
              disabled={isLoading}
            />
            {validationErrors.username && (
              <span className="validation-error">{validationErrors.username}</span>
            )}
            <small className="form-hint">로그인 시 사용할 고유한 계정 ID입니다. 한글, 영문, 숫자, 언더스코어(_) 사용 가능</small>
          </div>

          <div className="form-group">
            <label htmlFor="email">이메일 <span className="required-mark">*</span></label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="이메일을 입력하세요"
              required
              disabled={isLoading}
            />
            {validationErrors.email && (
              <span className="validation-error">{validationErrors.email}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="name">이름 <span className="required-mark">*</span></label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="실제 이름을 입력하세요"
              required
              disabled={isLoading}
            />
            {validationErrors.name && (
              <span className="validation-error">{validationErrors.name}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="nickname">닉네임 <span className="required-mark">*</span></label>
            <input
              type="text"
              id="nickname"
              name="nickname"
              value={formData.nickname}
              onChange={handleInputChange}
              placeholder="닉네임을 입력하세요"
              required
              disabled={isLoading}
            />
            {validationErrors.nickname && (
              <span className="validation-error">{validationErrors.nickname}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="phone">전화번호 <span className="required-mark">*</span></label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="01012345678 (하이픈 자동 입력)"
              maxLength={13}
              required
              disabled={isLoading}
            />
            {validationErrors.phone && (
              <span className="validation-error">{validationErrors.phone}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">비밀번호 <span className="required-mark">*</span></label>
            <div className="password-input-wrapper">
              <input
                type={isPasswordVisible ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="비밀번호를 입력하세요"
                required
                disabled={isLoading}
                className="password-input"
              />
              <button
                type="button"
                onPointerDown={() => setIsPasswordVisible(true)}
                onPointerUp={() => setIsPasswordVisible(false)}
                onPointerLeave={() => setIsPasswordVisible(false)}
                onContextMenu={(e) => e.preventDefault()}
                className={`password-toggle-btn ${isPasswordVisible ? 'active' : ''}`}
              >
                {isPasswordVisible ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor" opacity="0.5"/>
                    <path d="M2 2l20 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
                  </svg>
                )}
              </button>
            </div>
            {validationErrors.password && (
              <span className="validation-error">{validationErrors.password}</span>
            )}
            <small className="form-hint">비밀번호는 6자 이상이어야 합니다.</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">비밀번호 확인 <span className="required-mark">*</span></label>
            <div className="password-input-wrapper">
              <input
                type={isConfirmPasswordVisible ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="비밀번호를 다시 입력하세요"
                required
                disabled={isLoading}
                className="password-input"
              />
              <button
                type="button"
                onPointerDown={() => setIsConfirmPasswordVisible(true)}
                onPointerUp={() => setIsConfirmPasswordVisible(false)}
                onPointerLeave={() => setIsConfirmPasswordVisible(false)}
                onContextMenu={(e) => e.preventDefault()}
                className={`password-toggle-btn ${isConfirmPasswordVisible ? 'active' : ''}`}
              >
                {isConfirmPasswordVisible ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor" opacity="0.5"/>
                    <path d="M2 2l20 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
                  </svg>
                )}
              </button>
            </div>
            {validationErrors.confirmPassword && (
              <span className="validation-error">{validationErrors.confirmPassword}</span>
            )}
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={registerPasskey}
                onChange={(e) => setRegisterPasskey(e.target.checked)}
                disabled={isLoading}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span>패스키도 등록하기</span>
            </label>
            <small className="form-hint" style={{ marginLeft: '28px', display: 'block', marginTop: '4px' }}>
              패스키를 등록하면 비밀번호 없이 지문, Face ID, 보안 키로 로그인할 수 있습니다.
            </small>
          </div>

          <button 
            type="submit" 
            className="register-button"
            disabled={isLoading || !formData.username.trim() || !formData.password.trim() || !formData.confirmPassword.trim()}
          >
            {isLoading ? '회원가입 중...' : '회원가입'}
          </button>
        </form>

        {onSwitchToLogin && (
          <div className="switch-form">
            <p>이미 계정이 있으신가요?</p>
            <button 
              type="button" 
              className="switch-button"
              onClick={onSwitchToLogin}
              disabled={isLoading}
            >
              로그인
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegisterForm;
